import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompanyType, Role } from '@app/models/admin.model';
import {
  BaseEnumResponse,
  BaseMasterDataResponse,
} from '@app/models/shared.models';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalizedValue, PaginatedData } from '../+models/shared.models';

@Injectable({ providedIn: 'root' })
export class GenericDataService {
  private readonly API_URL = environment.apiUrl;

  constructor(private httpClient: HttpClient) {}

  public loadCountries() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/Countries?take=1000`,
    );
    return response;
  }

  public loadCountriesAdmin() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/CountriesAdmin?take=1000`,
    );
    return response;
  }

  public loadCurrencies() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/Currencies?take=1000`,
    );
    return response;
  }

  public loadCurrenciesAdmin() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/CurrenciesAdmin?take=1000`,
    );
    return response;
  }

  public loadLegalEntityRoleAdmin() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/LegalEntityRoleAdmin?take=1000`,
    );
    return response;
  }

  public loadNameTitles() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/NameTitle?take=1000`,
    );
    return response;
  }

  public loadNameTitlesAdmin() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/NameTitleAdmin?take=1000`,
    );
    return response;
  }

  public loadPersonRolesAdmin() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/PersonRoleAdmin?take=1000`,
    );
    return response;
  }

  public loadIdCardTypes() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/IdCardType?take=1000`,
    );
    return response;
  }

  public loadPublicSpaceTypes() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/PublicSpaceType?take=1000`,
    );
    return response;
  }

  public loadInvestorQualification() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/InvestorQualification?take=1000`,
    );
    return response;
  }

  public loadUserRepresentationTypes() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/UserRepresentation?take=1000`,
    );
    return response;
  }

  public loadCompanyTypes() {
    const response = this.httpClient.get<PaginatedData<CompanyType>>(
      `${this.API_URL}/CompanyTypes?take=1000`,
    );
    return response;
  }

  public loadCompanyTypesAdmin() {
    const response = this.httpClient.get<PaginatedData<CompanyType>>(
      `${this.API_URL}/CompanyTypesAdmin?take=1000`,
    );
    return response;
  }

  public projectClassifications() {
    return this.httpClient
      .get<
        PaginatedData<LocalizedValue>
      >(`${this.API_URL}/ProjectClassification?take=1000`)
      .pipe(map((res) => res?.data ?? []));
  }

  public loadProjectClassificationsAdmin() {
    const response = this.httpClient.get<PaginatedData<LocalizedValue>>(
      `${this.API_URL}/ProjectClassificationAdmin?take=1000`,
    );
    return response;
  }

  public loadProjectMeetingStatus() {
    return this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectMeetingStatus`,
    );
  }

  public loadProjectStatus() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectStatus`,
    );
    return response;
  }

  public loadProjectFounding() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectFounding`,
    );
    return response;
  }

  public loadLoanTypePaymentFrequency() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/LoanTypePaymentFrequency`,
    );
    return response;
  }

  public loadIncompleteCollectionCase() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/IncompleteCollectionCase`,
    );
    return response;
  }

  public loadFileType() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/FileType`,
    );
    return response;
  }

  public loadFinancialAssetType() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/FinancialAssetType`,
    );
    return response;
  }

  public loadInvestmentStatus() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/InvestmentStatus`,
    );
    return response;
  }

  public loadOwnershipStructure() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/OwnershipStructure`,
    );
    return response;
  }

  public loadLegalEntityInvolvement() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/LegalEntityInvolvement`,
    );
    return response;
  }

  public loadFinancialAssetProjectConnection() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/FinancialAssetProjectConnection`,
    );
    return response;
  }

  public loadProjectLoanCoverage() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectLoanCoverage`,
    );
    return response;
  }

  public loadProjectLoanUsage() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectLoanUsage`,
    );
    return response;
  }

  public loadProjectLoanTerm() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectLoanTerm`,
    );
    return response;
  }

  public loadProjectLoanInterestRatesType() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectLoanInterestRatesType`,
    );
    return response;
  }

  public loadProjectLoanInterestPaymentType() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ProjectLoanInterestPaymentType`,
    );
    return response;
  }
  public loadOversubscriptionType() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/OversubscriptionType`,
    );
    return response;
  }
  public loadSecuritiesType() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/SecuritiesType`,
    );
    return response;
  }
  public loadFinancialAssetConnectionTypeToProject() {
    const response = this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/FinancialAssetConnectionTypeToProject`,
    );
    return response;
  }

  public loadLanguages(): Observable<BaseMasterDataResponse[]> {
    return this.httpClient
      .get<PaginatedData<BaseMasterDataResponse>>(`${this.API_URL}/Languages`)
      .pipe(
        map((res) => {
          const data = res?.data ?? [];
          return data.sort((a, b) =>
            a.code === 'EN' ? -1 : b.code === 'EN' ? 1 : 0,
          );
        }),
      );
  }

  public loadRoles(): Observable<Role[]> {
    return this.httpClient
      .get<PaginatedData<Role>>(`${this.API_URL}/Auth/Roles?take=1000`)
      .pipe(map((res) => (Array.isArray(res) ? res : (res?.data ?? []))));
  }

  public loadContactComplaintTypes(): Observable<BaseEnumResponse[]> {
    return this.httpClient.get<BaseEnumResponse[]>(
      `${this.API_URL}/Basedata/ContactComplaintTypes?take=1000`,
    );
  }
}
